import { useEffect, useState, useCallback} from "react";
import {
  FaEdit,
  FaTimes,
  FaCheck,
  FaPlus,
  FaCog,
  FaToggleOn,
  FaToggleOff,
  FaTag,
  FaCode,
} from "react-icons/fa";
import { useApi } from "../context/useApi";

const API_BASE = "https://admin-backend-fbuuh6fo3q-el.a.run.app/settings";
const SettingsPanel = () => {
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
    const { apiFetch } = useApi();
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loadingSave, setLoadingSave] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [toast, setToast] = useState({ message: "", type: "" });
  const [loadingAdd, setLoadingAdd] = useState(false);
 

  const [newForm, setNewForm] = useState({
    setting_name: "",
    setting_value: "",
    type: "text",
    tags: "",
    remarks: "",
  });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

const fetchSettings = useCallback(async () => {
  setIsLoading(true);
  try {
    const res = await apiFetch(`${API_BASE}/get-settings`);
    if (!res) return;
    const data = await res.json();
    if (res.ok) setSettings(data.settings);
    else showToast(data.error || "Failed to fetch settings", "error");
  } catch (err) {
    console.error(err);
    showToast("Error fetching settings", "error");
  } finally {
    setIsLoading(false);
  }
}, [apiFetch]);
useEffect(() => {
  fetchSettings();
}, [fetchSettings]);

  const handleEdit = (setting) => {
    setEditing(setting.short_code);
    setEditForm({
      setting_value: setting.setting_value,
      remarks: setting.remarks || "",
      tags: setting.tags ? setting.tags.join(", ") : "",
    });
  };
  const handleCancel = () => {
    setEditing(null);
    setEditForm({});
  };

  const handleSave = async (short_code) => {
    setLoadingSave(true);
    try {
      const payload = {
        setting_value: editForm.setting_value,
        status: editForm.status,
        remarks: editForm.remarks,
        tags: editForm.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };

      const res = await apiFetch(`${API_BASE}/update-setting/${short_code}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        showToast("Setting updated successfully!");
        fetchSettings();
        setEditing(null);
      } else showToast(data.error || "Failed to update", "error");
    } catch (err) {
      showToast("Error updating setting", "error");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleAddSetting = async () => {
    setLoadingAdd(true);
    try {
      const payload = {
        setting_name: newForm.setting_name,
        setting_value: newForm.setting_value,
        type: newForm.type,
        tags: newForm.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        remarks: newForm.remarks,
      };
      const res = await apiFetch(`${API_BASE}/add-setting`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        showToast("Setting added!");
        fetchSettings();
        setNewForm({
          setting_name: "",
          setting_value: "",
          type: "text",
          tags: "",
          remarks: "",
        });
      } else showToast(data.error || "Failed to add setting", "error");
    } catch (err) {
      showToast("Error adding setting", "error");
    } finally {
      setLoadingAdd(false);
    }
  };

  // Filter settings
  const filteredSettings = settings.filter((setting) => {
    const matchesSearch =
      setting.setting_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      setting.short_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      setting.tags?.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesType = filterType === "all" || setting.type === filterType;

    return matchesSearch && matchesType;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case "boolean":
        return <FaToggleOn className="text-green-600" size={16} />;
      case "number":
        return <FaCode className="text-blue-600" size={16} />;
      default:
        return <FaTag className="text-gray-600" size={16} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage configuration values and feature flags • {settings.length}{" "}
              total
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm font-medium"
          >
            <FaPlus size={12} />
            Add Setting
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <FaCog className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Settings</p>
                <p className="text-2xl font-bold text-gray-900">
                  {settings.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <FaToggleOn className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Boolean Settings</p>
                <p className="text-2xl font-bold text-gray-900">
                  {settings.filter((s) => s.type === "boolean").length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <FaCode className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Config Values</p>
                <p className="text-2xl font-bold text-gray-900">
                  {settings.filter((s) => s.type !== "boolean").length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, code, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="text">Text</option>
            <option value="boolean">Boolean</option>
            <option value="number">Number</option>
          </select>
        </div>
      </div>

      {/* Settings List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="border-2 border-gray-200 rounded-lg p-5 animate-pulse"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="space-y-2">
                  <div className="h-5 w-48 border border-gray-300 rounded"></div>
                  <div className="h-4 w-64 border border-gray-300 rounded"></div>
                </div>
                <div className="h-8 w-20 border border-gray-300 rounded"></div>
              </div>
              <div className="h-10 w-full border border-gray-300 rounded"></div>
            </div>
          ))}
        </div>
      ) : filteredSettings.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-16 text-center">
          <div className="inline-block p-4 border-2 border-gray-300 rounded-full mb-4">
            <FaCog className="text-gray-400" size={48} />
          </div>
          <p className="text-gray-600 font-medium mb-1">No settings found</p>
          <p className="text-sm text-gray-500">
            {searchTerm
              ? "Try a different search term"
              : "Start by adding a new setting"}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSettings.map((setting) => {
            const isEditing = editing === setting.short_code;

            return (
              <div
                key={setting.id}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                          {getTypeIcon(setting.type)}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {setting.setting_name}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                              {setting.short_code}
                            </code>
                            <span className="text-xs text-gray-500">
                              • {setting.type}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {!isEditing && (
                      <button
                        onClick={() => handleEdit(setting)}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                      >
                        <FaEdit size={14} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Value Section */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">
                        Value
                      </label>
                      {isEditing ? (
                        setting.type === "boolean" ? (
                          <select
                            value={editForm.setting_value}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                setting_value: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          >
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        ) : setting.type === "number" ? (
                          <input
                            type="number"
                            value={editForm.setting_value}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                setting_value: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          />
                        ) : (
                          <input
                            type="text"
                            value={editForm.setting_value}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                setting_value: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          />
                        )
                      ) : setting.type === "boolean" ? (
                        <div className="flex items-center gap-2">
                          {setting.setting_value === "true" ? (
                            <FaToggleOn className="text-green-600" size={24} />
                          ) : (
                            <FaToggleOff className="text-gray-400" size={24} />
                          )}
                          <span
                            className={`font-medium ${
                              setting.setting_value === "true"
                                ? "text-green-700"
                                : "text-gray-600"
                            }`}
                          >
                            {setting.setting_value === "true"
                              ? "Enabled"
                              : "Disabled"}
                          </span>
                        </div>
                      ) : (
                        <code className="text-sm bg-gray-100 px-3 py-2 rounded block text-gray-900 break-all">
                          {setting.setting_value}
                        </code>
                      )}
                    </div>

                    {/* Tags Section */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">
                        Tags
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.tags}
                          onChange={(e) =>
                            setEditForm({ ...editForm, tags: e.target.value })
                          }
                          placeholder="Comma separated tags"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {setting.tags?.length ? (
                            setting.tags.map((tag, i) => (
                              <span
                                key={i}
                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400 text-xs">
                              No tags
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Remarks Section */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-500 mb-2">
                      Remarks
                    </label>
                    {isEditing ? (
                      <textarea
                        value={editForm.remarks}
                        onChange={(e) =>
                          setEditForm({ ...editForm, remarks: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        rows={2}
                      />
                    ) : (
                      <p className="text-sm text-gray-900">
                        {setting.remarks || "No remarks"}
                      </p>
                    )}
                  </div>

                  {/* Last Updated */}
                  <div className="text-xs text-gray-500 mb-4">
                    Last updated:{" "}
                    {new Date(setting.last_updated).toLocaleString()}
                  </div>

                  {/* Action Buttons */}
                  {isEditing && (
                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <button
                        onClick={handleCancel}
                        disabled={loadingSave}
                        className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSave(setting.short_code)}
                        disabled={loadingSave}
                        className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                      >
                        <FaCheck size={12} />
                        {loadingSave ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Setting Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Setting
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Setting Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Max Upload Size"
                  value={newForm.setting_name}
                  onChange={(e) =>
                    setNewForm({ ...newForm, setting_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Type *
                </label>
                <select
                  value={newForm.type}
                  onChange={(e) =>
                    setNewForm({
                      ...newForm,
                      type: e.target.value,
                      setting_value: "",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="text">Text</option>
                  <option value="boolean">Boolean</option>
                  <option value="number">Number</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Value *
                </label>
                {newForm.type === "boolean" ? (
                  <select
                    value={newForm.setting_value}
                    onChange={(e) =>
                      setNewForm({ ...newForm, setting_value: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Select Value</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : newForm.type === "number" ? (
                  <input
                    type="number"
                    placeholder="Enter number value"
                    value={newForm.setting_value}
                    onChange={(e) =>
                      setNewForm({ ...newForm, setting_value: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="Enter text value"
                    value={newForm.setting_value}
                    onChange={(e) =>
                      setNewForm({ ...newForm, setting_value: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tags
                </label>
                <input
                  type="text"
                  placeholder="Comma separated tags"
                  value={newForm.tags}
                  onChange={(e) =>
                    setNewForm({ ...newForm, tags: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Remarks
                </label>
                <textarea
                  placeholder="Add any notes or remarks..."
                  value={newForm.remarks}
                  onChange={(e) =>
                    setNewForm({ ...newForm, remarks: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  disabled={loadingAdd}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSetting}
                  disabled={loadingAdd}
                  className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                >
                  <FaCheck size={12} />
                  {loadingAdd ? "Adding..." : "Add Setting"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.message && (
        <div
          className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
            toast.type === "success" ? "bg-gray-900" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;
